import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MapPin, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PincodeCheck = ({ pincode: controlledPincode, onResult, compact = false, testIdPrefix = 'pincode' }) => {
  const [pincode, setPincode] = useState(controlledPincode || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = useCallback(async (pc) => {
    const value = (pc ?? pincode).trim();
    if (value.length !== 6 || !/^\d{6}$/.test(value)) {
      setResult({ serviceable: false, message: 'Please enter a valid 6-digit pincode.' });
      onResult?.({ serviceable: false, pincode: value, message: 'Please enter a valid 6-digit pincode.' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/shipping/serviceability`, { params: { pincode: value } });
      setResult(data);
      onResult?.(data);
    } catch {
      const fallback = { serviceable: false, pincode: value, message: 'Unable to check serviceability. Try again.' };
      setResult(fallback);
      onResult?.(fallback);
    } finally {
      setLoading(false);
    }
  }, [pincode, onResult]);

  // Auto-check when controlled pincode changes (used by Cart when address changes)
  useEffect(() => {
    if (controlledPincode && controlledPincode !== pincode) {
      setPincode(controlledPincode);
      check(controlledPincode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledPincode]);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-check-wrapper`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={pincode}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 6);
              setPincode(v);
              // Invalidate stale result the moment the user edits the input
              if (result) {
                setResult(null);
                onResult?.(null);
              }
            }}
            placeholder="Enter 6-digit pincode"
            className="pl-9"
            data-testid={`${testIdPrefix}-input`}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } }}
          />
        </div>
        <Button
          variant="outline"
          size={compact ? 'sm' : 'default'}
          onClick={() => check()}
          disabled={loading || pincode.length !== 6}
          data-testid={`${testIdPrefix}-check-btn`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
        </Button>
      </div>

      {result && (
        result.serviceable ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid={`${testIdPrefix}-result-serviceable`}>
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-semibold text-primary">Deliverable to {result.pincode}</p>
              <p className="text-muted-foreground mt-0.5">
                {`Delivers in ${result.estimated_days} days`}
                {result.min_rate > 0 ? ` · starts at \u20B9${Math.round(result.min_rate)}` : ''}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20" data-testid={`${testIdPrefix}-result-unserviceable`}>
            <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-xs text-destructive">{result.message || 'Not deliverable to this pincode.'}</p>
          </div>
        )
      )}
    </div>
  );
};

export default PincodeCheck;
